/**
 * Базовая конфигурация справочника.
 *
 * Наследник описывает конкретную сущность: через какое api её читать и писать,
 * какие колонки показывать в попапе выбора и какие поля рисовать в форме
 * элемента. Компонент BaseDir ничего не знает о сущности — он работает только
 * с этим интерфейсом.
 *
 * Не всё можно зафиксировать в статическом конфиге: опции динамических
 * выпадающих списков, фильтры вложенных справочников и т.п. приходят снаружи
 * в объекте `context`, который BaseDir прокидывает в getFields()/getColumns().
 * Предварительная фильтрация списка (например, «только клиенты этой компании»)
 * передаётся отдельно, в `filter`, и уходит в getList() как параметры запроса.
 */
export default class BaseDir {
  /** Заголовок попапа со списком элементов. */
  get title() { return 'Справочник'; }

  /** Заголовок попапа с формой одного элемента. */
  get itemTitle() { return 'Элемент справочника'; }

  /** Синглтон из src/api, через который работает справочник. */
  // eslint-disable-next-line class-methods-use-this
  get api() { throw new Error('Must be implemented'); }

  getList(filter = {}) {
    return this.api.fetchList(filter, true).then(r => r.items);
  }

  getItem(id) {
    return this.api.fetchItem(id, {}, true);
  }

  create(values, filter = {}, context = {}) {
    return this.api.create(this.toRequestValues(values, filter, context), true);
  }

  update(id, values, filter = {}, context = {}) {
    return this.api.update(id, this.toRequestValues(values, filter, context), true);
  }

  remove(id) {
    return this.api.delete(id, true);
  }

  /**
   * Конфигурация формы элемента: объект вида { имяПоля: form.createFieldObject(...) }.
   * Отдаётся в BaseForm и совместим с утилитами src/utils/form.js.
   *
   * item — редактируемый элемент или null при создании: нужен тем конфигам, у
   * которых состав полей на создании и на правке различается.
   */
  // eslint-disable-next-line class-methods-use-this, no-unused-vars
  getFields(context = {}, item = null) { throw new Error('Must be implemented'); }

  /**
   * Конфигурация колонок таблицы. Формат совместим с headers у BaseTable:
   *   title     — заголовок колонки
   *   width     — ширина в процентах
   *   sortFn    — метод сортировки по колонке, (a, b) => number
   *   render    — метод визуализации ячейки строкой, (item) => string | number
   *   component — метод визуализации ячейки компонентом: { is, props(item) }.
   *               Задан — рисуем им, render в этом случае не нужен
   * defaultSort/defaultOrder тоже работают — их читает сам BaseTable.
   */
  // eslint-disable-next-line no-unused-vars
  getColumns(context = {}) {
    return [
      {
        title: 'Название',
        sortFn: (a, b) => this.getLabel(a).localeCompare(this.getLabel(b)),
        render: item => this.getLabel(item),
      },
    ];
  }

  /** Что справочник отдаёт наружу как значение (v-model). */
  // eslint-disable-next-line class-methods-use-this
  getValue(item) { return item?.['@id'] ?? item?.id ?? null; }

  /** Как подписать выбранный элемент в поле и в подтверждении удаления. */
  // eslint-disable-next-line class-methods-use-this
  getLabel(item) { return item?.name ?? ''; }

  /** По какой строке искать элемент в попапе со списком. */
  getSearchIndex(item) { return this.getLabel(item); }

  /** Сущность из api -> значения формы (по умолчанию — поля с теми же именами). */
  getFormValues(item, context = {}) {
    const names = Object.keys(this.getFields(context));
    return Object.fromEntries(names.map(name => [name, item?.[name] ?? null]));
  }

  /** Значения формы -> тело запроса. */
  // eslint-disable-next-line class-methods-use-this, no-unused-vars
  toRequestValues(values, filter = {}, context = {}) { return values; }
}
